import type { PetEnergyColor } from '@kenocyber/shared';

/**
 * The energy colour, as a plain number, for anything that is not Three.js.
 *
 * The 3D model already animates its emissive materials from the equipped
 * energy. Its bullets, muzzle flashes, trails and impacts are drawn by PHASER,
 * which cannot import `pet-materials.ts` - that module lives behind the lazy
 * Three.js chunk, and pulling it into the arena would put the whole library in
 * the main bundle for players who own no pet.
 *
 * So the ramp lives here, framework-free, and `pet-materials.ts` takes the
 * CYCLE LENGTH from this module rather than declaring a second one. The shapes
 * are the approved ones: an asymmetric two-sine furnace for Anomaly, a fixed
 * saturation-and-lightness hue sweep for Prismatic, and the catalog colour
 * itself for the eight fixed palettes.
 *
 * Presentation only. Damage, targeting, cadence and hit resolution are the
 * simulation's, and nothing here is read by any of them.
 */

/**
 * One full trip around the spectrum, milliseconds.
 *
 * The single source for the pet's animated palettes: `pet-materials.ts`
 * re-exports it as `PRISMATIC_ENERGY_CYCLE_MS`, so the bolt leaving the gun and
 * the gun that fired it cycle at the same rate by construction.
 */
export const PET_ENERGY_CYCLE_MS = 2200;

/**
 * How far each part of a shot lags the bolt itself, in turns.
 *
 * Without this a Prismatic volley is one flat colour: bolt, wake, flash and
 * spark all cyan at once. Offsetting them means a frame shows a green bolt
 * trailing violet with a gold flash still at the muzzle, which is what the
 * design asks for and what the model already does with its own components.
 */
export const PET_BULLET_PHASE_TURNS = Object.freeze({
  bolt: 0,
  trail: 0.18,
  muzzle: 0.09,
  impact: 0.27,
});

/**
 * The same idea for the BEAM's layers.
 *
 * A continuous laser is one object made of five drawn parts, and giving each
 * of them the same hue at the same instant would make an animated palette read
 * as a lamp on a dimmer. Offsetting them puts two colours on the beam at once -
 * which is exactly what Anomaly is meant to look like ("amarelo e vermelho
 * simultaneamente") and what keeps Prismatic from being one hue that changes.
 *
 * The core stays at zero and is drawn near-white on top of its colour, so the
 * middle of the beam never becomes a plain coloured line.
 */
export const PET_BEAM_PHASE_TURNS = Object.freeze({
  core: 0,
  inner: 0.11,
  halo: 0.22,
  pulse: 0.16,
  impact: 0.3,
});

/**
 * How long the beam takes to travel to a newly chosen energy, milliseconds.
 *
 * A beam is CONNECTED to the pet rather than launched from it, so it does not
 * follow the frozen-colour rule its projectiles do - there is nothing in flight
 * to be repainted retroactively. It simply changes colour, and it does so over
 * a moment rather than between two frames.
 */
export const PET_BEAM_COLOR_FADE_MS = 150;

/**
 * How many places an animated ramp is allowed to stop at.
 *
 * The dash artwork BAKES its colour into a texture - `setTint` would multiply
 * the near-white core down to the body colour and destroy the layering the
 * neon look depends on - so a palette that changed continuously would ask for
 * a new texture every frame and never stop.
 *
 * The fix is to quantise the ramp's INPUT rather than its output, which bounds
 * the texture count exactly: an animated energy can ever produce at most this
 * many colours, generated on first use and reused for the rest of the session.
 * At 64 steps the Prismatic hue moves 5.6 degrees at a time, about two frames
 * apart at 60 fps - a rounding, not a jump, and far below what reads as a
 * flash.
 *
 * Fixed palettes are NOT quantised: they are drawn in exactly the colour the
 * catalog states, and they need one texture each.
 */
export const PET_ENERGY_RAMP_STEPS = 64;

function quantise(value: number): number {
  return Math.round(value * PET_ENERGY_RAMP_STEPS) / PET_ENERGY_RAMP_STEPS;
}

function pack(r: number, g: number, b: number): number {
  const clamp = (channel: number) => Math.min(255, Math.max(0, Math.round(channel)));
  return (clamp(r) << 16) | (clamp(g) << 8) | clamp(b);
}

/** Hue-saturation-lightness to a packed colour, the usual formulation. */
export function hslToColor(hue: number, saturation: number, lightness: number): number {
  const h = ((hue % 1) + 1) % 1;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = lightness - c / 2;
  const sector = Math.floor(h * 6) % 6;
  const [r, g, b] = (
    [
      [c, x, 0],
      [x, c, 0],
      [0, c, x],
      [0, x, c],
      [x, 0, c],
      [c, 0, x],
    ] as const
  )[sector] ?? [c, x, 0];
  return pack((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

/** A packed colour to hue, saturation and lightness in [0, 1]. */
export function colorToHsl(color: number): { h: number; s: number; l: number } {
  const r = ((color >> 16) & 0xff) / 255;
  const g = ((color >> 8) & 0xff) / 255;
  const b = (color & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) {
    h = (g - b) / d + (g < b ? 6 : 0);
  } else if (max === g) {
    h = (b - r) / d + 2;
  } else {
    h = (r - g) / d + 4;
  }
  return { h: h / 6, s, l };
}

function lerpColor(from: number, to: number, amount: number): number {
  const mix = (shift: number) => {
    const a = (from >> shift) & 0xff;
    const b = (to >> shift) & 0xff;
    return a + (b - a) * amount;
  };
  return pack(mix(16), mix(8), mix(0));
}

/**
 * What colour a pet's shot is, for one energy at one moment.
 *
 * `milliseconds` is the simulation's own clock, so two clients at the same tick
 * draw the same bolt and a hidden tab does not advance the palette. `phaseTurns`
 * offsets one part of the shot against the others.
 *
 * The eight fixed palettes ignore both arguments entirely - a Red Plasma bolt
 * is the catalog's red, always.
 */
export function petEnergyColorAt(
  energy: PetEnergyColor,
  milliseconds: number,
  phaseTurns = 0,
): number {
  if (!energy.animated) {
    return energy.color;
  }
  const seconds = milliseconds / 1000 + phaseTurns * (PET_ENERGY_CYCLE_MS / 1000);

  if (energy.secondaryColor !== null) {
    /*
     * Anomaly: red through orange to gold, and never a two-colour ping-pong.
     *
     * The same asymmetric pair of incommensurate sines the model uses, so the
     * bolt and the orb that fired it are the same fire. It lingers deep red,
     * flares to gold, and passes through orange at different rates on the way
     * out and back. No randomness anywhere: the same tick gives the same colour
     * on every machine.
     */
    const wave = (Math.sin(seconds * 2.1) + Math.sin(seconds * 3.7) * 0.5) / 1.5;
    const t = quantise(Math.min(1, Math.max(0, (wave + 1) / 2) ** 1.6));
    const mixed = lerpColor(energy.color, energy.secondaryColor, t);
    // Lerping red to gold in RGB dips through a muddy middle; the mix is lifted
    // back to full saturation so the pass is through orange, not through brown.
    const { h, s, l } = colorToHsl(mixed);
    return hslToColor(h, Math.max(s, 0.92), Math.max(l, 0.52));
  }

  /*
   * Prismatic: the whole spectrum at fixed saturation and lightness, so it
   * never washes out to white or drops to black.
   */
  const turns = (seconds * 1000) / PET_ENERGY_CYCLE_MS;
  return hslToColor(quantise(((turns % 1) + 1) % 1), 0.95, 0.58);
}

/**
 * A stable per-shot offset, so two bolts in the same volley are not identical.
 *
 * Derived from the projectile's own id rather than from a random number: the
 * same shot is the same colour on every frame, on every client, and after a
 * re-simulation. The multiplier is irrational enough that consecutive ids land
 * far apart on the ramp instead of walking it in tiny steps.
 */
export function bulletPhaseTurns(projectileId: number): number {
  return (projectileId * 0.381966) % 1;
}
